import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, User, Linkedin, AlertCircle, BookOpen } from "lucide-react";
import { api } from "@/lib/api";

export default function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/blogs/${slug}`)
      .then((r) => {
        setBlog(r.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching blog detail:", err);
        setError("Unable to retrieve the requested blog article. It might have been deleted or the slug is invalid.");
        setLoading(false);
      });
  }, [slug]);

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch (e) {
      return dateStr;
    }
  };

  const renderContent = (content) => {
    if (!content) return null;

    const isHtml = /<[a-z][\s\S]*>/i.test(content);
    if (isHtml) {
      return (
        <div 
          className="wysiwyg-content prose prose-indigo max-w-none text-slate-700 leading-relaxed text-base sm:text-lg font-medium space-y-4"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    }

    return content.split("\n\n").map((block, idx) => {
      // Heading level 3
      if (block.startsWith("### ")) {
        return (
          <h3 key={idx} className="text-xl sm:text-2xl font-bold text-slate-800 mt-8 mb-4 border-b border-slate-100 pb-2">
            {block.replace("### ", "")}
          </h3>
        );
      }
      // Heading level 2
      if (block.startsWith("## ")) {
        return (
          <h2 key={idx} className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-10 mb-5 border-b border-slate-200 pb-2">
            {block.replace("## ", "")}
          </h2>
        );
      }
      // Bullet list item
      if (block.startsWith("- ") || block.startsWith("* ")) {
        const items = block.split("\n");
        return (
          <ul key={idx} className="list-disc list-inside space-y-2 my-5 text-slate-600 pl-4 font-medium">
            {items.map((item, itemIdx) => (
              <li key={itemIdx} className="leading-relaxed">
                {item.replace(/^[-*]\s+/, "")}
              </li>
            ))}
          </ul>
        );
      }
      // Numbered list item
      if (/^\d+\.\s+/.test(block)) {
        const items = block.split("\n");
        return (
          <ol key={idx} className="list-decimal list-inside space-y-2 my-5 text-slate-600 pl-4 font-medium">
            {items.map((item, itemIdx) => (
              <li key={itemIdx} className="leading-relaxed">
                {item.replace(/^\d+\.\s+/, "")}
              </li>
            ))}
          </ol>
        );
      }
      // Default paragraph
      return (
        <p key={idx} className="text-slate-600 leading-relaxed my-5 text-base sm:text-lg font-medium">
          {block}
        </p>
      );
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8 flex flex-col justify-center items-center">
        <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        <p className="mt-4 text-slate-500 font-semibold animate-pulse">Loading article detail...</p>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center bg-white border border-sif p-8 rounded-2xl card-shadow mt-10">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <h2 className="mt-4 text-2xl font-bold text-slate-800">Failed to Load Article</h2>
          <p className="mt-2 text-slate-600">{error || "Article not found."}</p>
          <div className="mt-6">
            <Link
              to="/blogs"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Blogs</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 pb-16">
      {/* Article Header Container */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
        {/* Breadcrumb Back Button */}
        <Link
          to="/blogs"
          className="inline-flex items-center gap-2 text-sm font-bold text-indigo-600 hover:text-indigo-800 transition mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to all insights</span>
        </Link>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight tracking-tight">
          {blog.title}
        </h1>

        {/* Subtitle/Extract */}
        <p className="mt-4 text-lg sm:text-xl text-slate-500 font-semibold leading-relaxed">
          {blog.extract}
        </p>

        {/* Author details / Metadata Bar */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 py-4 border-y border-slate-200">
          <div className="flex items-center gap-3">
            {/* Author Profile initials */}
            <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-indigo-500 to-indigo-700 text-white flex items-center justify-center font-bold text-sm shadow-sm select-none">
              {blog.author.split(" ").map(n => n[0]).join("")}
            </div>
            <div>
              <div className="text-sm font-bold text-slate-800">{blog.author}</div>
              <div className="text-xs text-sif-muted font-medium flex items-center gap-1 mt-0.5">
                <Calendar className="w-3 h-3 text-indigo-500" />
                <span>Published on {formatDate(blog.date)}</span>
              </div>
            </div>
          </div>

          {/* LinkedIn Button */}
          {blog.linkedin_url && (
            <a
              href={blog.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-50 hover:bg-blue-100/80 border border-blue-200 text-xs font-bold text-blue-700 transition-colors"
            >
              <Linkedin className="w-4 h-4 fill-blue-700 text-blue-700 stroke-1" />
              <span>Connect on LinkedIn</span>
            </a>
          )}
        </div>
      </div>

      {/* Full-width Hero Cover Image */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="relative rounded-2xl overflow-hidden shadow-md border border-sif aspect-video bg-slate-100">
          <img
            src={blog.image}
            alt={blog.title}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Main Content Article Body */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <article className="bg-white rounded-2xl border border-sif card-shadow p-6 sm:p-10 text-slate-700">
          <div className="prose prose-indigo prose-lg max-w-none">
            {renderContent(blog.content)}
          </div>
          
          {/* Footer note */}
          <div className="mt-12 pt-8 border-t border-slate-100 text-center">
            <div className="inline-flex items-center gap-2 text-xs font-bold text-sif-secondary uppercase tracking-widest bg-slate-50 px-4 py-2 rounded-full border border-slate-150">
              <BookOpen className="w-4 h-4 text-sif-secondary" />
              <span>SIF Intelligence Platform</span>
            </div>
            <p className="text-xs text-sif-muted mt-2 font-medium">
              We deliver real-time data, insights, and analysis for Specialised Investment Funds.
            </p>
          </div>
        </article>

        {/* Back Link at bottom */}
        <div className="mt-8 text-center">
          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-white border border-sif-primary/20 text-indigo-600 hover:text-indigo-800 hover:bg-slate-50 rounded-xl font-bold transition card-shadow"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Knowledge Hub</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
